// Data-mapping helpers + the two direct API calls that don't fit the saga model
// (attachment upload needs onUploadProgress; transcript is a binary download).
// Everything else flows through the redux saga (support_saga.js).
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import { getCookie } from '../../../util_functions';
import { get_api_url } from '../../../utils/urls';
import { modules as quillModules, formats as quillFormats } from '../../../constants';

const authHeaders = () => ({ Authorization: `Bearer ${getCookie('cms_token_3')}` });

// Sanitize rich-text HTML before render/send (keeps target/rel on links).
export const cleanHtml = (html) => DOMPurify.sanitize(html || '', { ADD_ATTR: ['target', 'rel'] });

// Render-only sanitizer for inbound/outbound email HTML, which is untrusted.
// Deliberately a SEPARATE DOMPurify instance from cleanHtml: cleanHtml also runs
// on the send path, where forcing target/rel would alter the HTML the customer
// receives, and a global hook would leak into every other caller.
const messagePurify = DOMPurify(window);

// Set for the duration of one sanitize() call so the hook below can resolve
// cid: sources. sanitize() is synchronous, so this cannot interleave.
let cidUrls = {};

messagePurify.addHook('afterSanitizeAttributes', (node) => {
  // Links in a message always leave the CMS in a new tab.
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  if (node.tagName !== 'IMG') return;
  const src = node.getAttribute('src') || '';
  if (!/^cid:/i.test(src)) return;
  // Outbound mail stores inline images as cid: references. Swap in the
  // attachment's signed URL when we can resolve it; otherwise drop the node
  // entirely, since a bare cid: src only renders a broken-image icon.
  const url = cidUrls[src.slice(4).replace(/^<|>$/g, '').toLowerCase()];
  if (url) node.setAttribute('src', url);
  else node.remove();
});

// The thread JSON carries no content_id today, so accept either spelling and
// simply resolve nothing when the backend has not started sending one.
const cidMapFor = (attachments) =>
  (attachments || []).reduce((acc, a) => {
    const cid = String((a && (a.content_id || a.cid)) || '')
      .replace(/^<|>$/g, '')
      .toLowerCase();
    if (cid && a.url) acc[cid] = a.url;
    return acc;
  }, {});

// `style` is dropped because .si-bubble is not a containing block: a
// `position: fixed` rule in email HTML would otherwise overlay the whole CMS
// (clickjacking, and a background-image is a tracking pixel). Form controls are
// dropped so a phishing form cannot render inside a thread. support_inbox.css
// re-asserts both as a defensive second layer.
export const cleanMessageHtml = (html, attachments) => {
  cidUrls = cidMapFor(attachments);
  try {
    return messagePurify.sanitize(html || '', {
      ADD_ATTR: ['target', 'rel'],
      FORBID_ATTR: ['style'],
      FORBID_TAGS: ['form', 'input', 'button', 'textarea', 'select', 'option']
    });
  } finally {
    cidUrls = {};
  }
};

// A <textarea> exposes no caret geometry, so mirror it in a hidden div with the
// same text metrics and measure a marker span at the caret offset. Returns
// viewport coordinates, for positioning a `fixed` popup at the caret.
const CARET_MIRROR_PROPS = [
  'width',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textTransform',
  'textIndent',
  'wordSpacing'
];

export const caretRect = (el) => {
  const rect = el.getBoundingClientRect();
  const cs = window.getComputedStyle(el);
  const mirror = document.createElement('div');
  CARET_MIRROR_PROPS.forEach((prop) => {
    mirror.style[prop] = cs[prop];
  });
  // getComputedStyle width is the content box, so pin content-box to match it.
  mirror.style.boxSizing = 'content-box';
  mirror.style.position = 'absolute';
  mirror.style.top = '0';
  mirror.style.left = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.overflowWrap = 'break-word';

  const caret = el.selectionStart;
  mirror.textContent = el.value.slice(0, caret);
  const marker = document.createElement('span');
  // Non-empty so the span has a box to measure.
  marker.textContent = el.value.slice(caret) || '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const top = rect.top + marker.offsetTop - el.scrollTop;
  const left = rect.left + marker.offsetLeft - el.scrollLeft;
  document.body.removeChild(mirror);

  const height = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) || 16;
  return { top, left, height };
};

// ------------------------------------------------------------ message bodies
// One newline must stay one newline across all four surfaces: the mini textarea
// (`\n`), the expanded Quill editor (one block per line, margins zeroed by
// quill.snow), the sent email and the message list. The canonical wire format is
// therefore "one block element per visual line, no paragraph margins" — the same
// shape Gmail sends. `<p>` cannot carry it: the app's global
// `p { margin-block: 1em }` (src/index.css) and most email clients' defaults add
// a full blank line around every paragraph, which is what doubled every newline.
const BLOCK_TAGS = 'p|div|h[1-6]|li|blockquote|pre|tr';
// A `<br>` that only fills an otherwise empty block (`<p><br></p>`) is that
// block's own line break, not an extra one.
const FILLER_BR_RE = new RegExp(`<br\\s*/?>\\s*(?=</(?:${BLOCK_TAGS})>)`, 'gi');
const BLOCK_CLOSE_RE = new RegExp(`</(?:${BLOCK_TAGS})>`, 'gi');

const escapeHtml = (text) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Derive a plain-text body from rich HTML (block tags → newlines, then read
// textContent). Trailing newlines are dropped; leading/inner whitespace is kept
// so the round-trip with textToHtmlBody is lossless.
export const htmlToPlainText = (html) => {
  const div = document.createElement('div');
  div.innerHTML = (html || '')
    .replace(FILLER_BR_RE, '')
    .replace(BLOCK_CLOSE_RE, (tag) => `${tag}\n`)
    .replace(/<br\s*\/?>/gi, '\n');
  return (div.textContent || '').replace(/\n+$/, '');
};

// Plain text → canonical body HTML: one `<div>` per line, an empty line kept as
// `<div><br></div>`.
export const textToHtmlBody = (text) =>
  (text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => `<div>${escapeHtml(line) || '<br>'}</div>`)
    .join('');

// Rich (Quill) HTML → canonical body HTML. Quill emits `<p>` per line and styles
// it with `margin: 0` inside its editor; rewriting those blocks to `<div>` keeps
// that exact spacing everywhere else too (message list, mailbox), while leaving
// inline formatting, lists and headings untouched.
export const canonicalBodyHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  Array.from(div.querySelectorAll('p')).forEach((p) => {
    const block = document.createElement('div');
    Array.from(p.attributes).forEach((attr) => block.setAttribute(attr.name, attr.value));
    while (p.firstChild) block.appendChild(p.firstChild);
    p.replaceWith(block);
  });
  return div.innerHTML;
};

// Small dropdown/popup helper: open state + a ref that closes on outside-click
// or Escape.
export const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
};

// ------------------------------------------------------- rich-editor pasting
// Quill collapses every pasted block element to a single newline, and its
// clipboard staging area resets paragraph margins (`.ql-clipboard p { margin: 0 }`
// in quill.core.css), so its own "was this block visually spaced?" heuristic can
// never fire. Content copied from an AI chat / web page therefore lands in the
// composer with its blank lines removed. Restore them at paste time, so what the
// agent sees while composing is what the customer receives:
//   * prose with no formatting in the text/html flavour is inserted from the
//     text/plain flavour instead — those newlines are the author's own;
//   * anything formatted keeps Quill's rich paste, with a matcher that turns a
//     block's own declared bottom margin into a real empty line.
const BLANK_LINE_RE = /\n[ \t]*\n/;
const GAP_BLOCK_TAGS = ['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'];

const declaresBottomGap = (node) => {
  const style = node.style || {};
  const margin = style.marginBottom || style.marginBlockEnd || '';
  return !!margin && parseFloat(margin) > 0;
};

const endsWithBlankLine = (delta) => {
  const last = delta.ops[delta.ops.length - 1];
  return !!last && typeof last.insert === 'string' && last.insert.endsWith('\n\n');
};

// Support composers never embed images. Gmail strips `data:` image sources, so
// a pasted screenshot silently vanishes for the recipient; images must travel as
// real attachments instead. Dropping the toolbar button AND 'image' from the
// allowed formats means Quill cannot embed one by any route — button, paste or
// drop. The shared constants stay untouched for other pages.
export const supportQuillModules = {
  ...quillModules,
  toolbar: quillModules.toolbar
    .map((group) => group.filter((item) => item !== 'image'))
    .filter((group) => group.length > 0)
};
export const supportQuillFormats = quillFormats.filter((f) => f !== 'image');

// Belt and braces for bodies built by an older path: a data: image would be
// forwarded to Resend verbatim and stripped in transit.
export const hasDataImage = (html) => /<img[^>]*\ssrc\s*=\s*["']?\s*data:/i.test(html || '');

const imageFilesFrom = (list) =>
  Array.from(list || []).filter((f) => f && String(f.type || '').startsWith('image/'));

// Attach to a ReactQuill ref. The paste listener sits on the wrapper in the
// capture phase so it can pre-empt Quill's own root listener. `mounted` re-runs
// the wiring for editors that mount later (the expanded composer's Quill only
// exists while the modal is open).
export const useQuillPaste = (quillRef, mounted = true, onImageFile = null) => {
  // Held in a ref so a new callback identity per render does not re-wire the
  // listeners (the effect deliberately runs only on `mounted`).
  const imageCb = useRef(onImageFile);
  imageCb.current = onImageFile;

  useEffect(() => {
    const editor = mounted && quillRef.current && quillRef.current.getEditor();
    if (!editor) return undefined;

    editor.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
      if (
        GAP_BLOCK_TAGS.includes(node.tagName) &&
        node.nextElementSibling &&
        declaresBottomGap(node) &&
        !endsWithBlankLine(delta)
      ) {
        delta.insert('\n');
      }
      return delta;
    });

    // Convenience only: 'image' is not an allowed format, so Quill would drop
    // the embed anyway — this turns it into an attachment instead of nothing.
    const takeImages = (e, list) => {
      const images = imageCb.current ? imageFilesFrom(list) : [];
      if (images.length === 0) return false;
      e.preventDefault();
      e.stopPropagation();
      images.forEach((file) => imageCb.current(file));
      return true;
    };

    const onDrop = (e) => {
      if (!editor.root.contains(e.target)) return;
      takeImages(e, e.dataTransfer && e.dataTransfer.files);
    };

    const onPaste = (e) => {
      if (!editor.root.contains(e.target)) return;
      const data = e.clipboardData;
      if (!data) return;
      if (takeImages(e, data.files)) return;
      const plain = (data.getData('text/plain') || '').replace(/\r\n?/g, '\n');
      const html = data.getData('text/html');
      if (!html || !BLANK_LINE_RE.test(plain)) return;
      // Only give up the rich paste when there is no formatting to lose.
      const { ops } = editor.clipboard.convert(html);
      if (ops.some((op) => op.attributes || typeof op.insert !== 'string')) return;
      e.preventDefault();
      e.stopPropagation();
      const range = editor.getSelection(true) || { index: editor.getLength() - 1, length: 0 };
      if (range.length) editor.deleteText(range.index, range.length, 'user');
      editor.insertText(range.index, plain, 'user');
      editor.setSelection(range.index + plain.length, 0, 'silent');
    };

    const wrap = editor.root.parentNode;
    wrap.addEventListener('paste', onPaste, true);
    wrap.addEventListener('drop', onDrop, true);
    return () => {
      wrap.removeEventListener('paste', onPaste, true);
      wrap.removeEventListener('drop', onDrop, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);
};

// crm_reminder_department rows are serialised by GORM without json tags, so keys
// arrive PascalCase (UUID/Name). Tolerate both shapes.
export const teamUuid = (team) => team.uuid || team.UUID || '';
export const teamName = (team) => team.name || team.Name || '';

// Left-nav team options. No emoji/colour comes from the backend, so the emoji
// column the wireframe used is dropped (kept as '' for layout compatibility).
export const mapTeams = (teams = []) =>
  teams.map((t) => ({ key: teamUuid(t), label: teamName(t), emoji: '' }));

// Assignable agents → picker options. `you` is resolved by matching the logged-in
// CMS user's email (bootstrap agents don't carry a "you" flag).
export const mapAgents = (agents = [], myEmail = '') =>
  agents.map((a) => ({
    key: a.uuid,
    label: a.name,
    email: a.email,
    you: !!myEmail && a.email && a.email.toLowerCase() === myEmail.toLowerCase()
  }));

// Tag pool → list of names for the tag picker.
export const mapTagPool = (tags = []) => tags.map((t) => t.name);

// Resolve a conversation contact to its CMS profile link. The GET conversation
// payload's `contact` carries {type, student, teacher}; an older payload had
// only `student`, so a missing type falls back to student-if-present else guest.
// Guests (and linked people missing a uuid) are not linkable — href is null.
// Routes match the app router: /studentprofile?id= and /teacherprofile?id=.
export const contactProfile = (contact) => {
  const c = contact || {};
  const type = c.type || (c.student ? 'student' : 'guest');
  let person = null;
  if (type === 'student') person = c.student;
  else if (type === 'teacher') person = c.teacher;
  const uuid = person && person.uuid;
  if (uuid && (type === 'student' || type === 'teacher')) {
    const base = type === 'student' ? '/studentprofile' : '/teacherprofile';
    return { type, href: `${base}?id=${encodeURIComponent(uuid)}` };
  }
  return { type: type === 'student' || type === 'teacher' ? type : 'guest', href: null };
};

// Our backend's default profile image, used as the single fallback when a
// person carries no real photo or the photo URL fails to load. The backend now
// always returns a non-empty `avatar` (real photo or this default), so this is
// only a defensive fallback for missing fields / broken image loads.
export const DEFAULT_AVATAR =
  'https://cloudimages.myyogateacher.com/dgerdfai4/image/upload/v1587531490/ic_default_profile.png';

// First-line display name for a support contact. Priority:
//   (a) the conversation's stored contact name (from the inbound From display
//       name / create payload),
//   (b) the resolved student/teacher full name, when the details `contact`
//       payload ({type, student, teacher}) carries one,
//   (c) the email's local-part (before the @) as a last resort, so we never
//       echo the raw email — which is already shown on its own row right below.
// `resolved` is optional: list rows only have {name, email} and pass nothing.
export const contactDisplayName = (contact, resolved) => {
  const c = contact || {};
  const name = (c.name || '').trim();
  if (name) return name;

  const r = resolved || {};
  let { type } = r;
  if (!type) {
    if (r.student) type = 'student';
    else if (r.teacher) type = 'teacher';
    else type = 'guest';
  }
  const person = type === 'teacher' ? r.teacher : r.student;
  const personName = person && (person.name || '').trim();
  if (personName) return personName;

  const email = (c.email || '').trim();
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
};

// Real profile photo for a thread-detail contact ({type, student, teacher}).
// Picks the avatar of whichever person matches the contact type; falls back to
// the default image when absent. Tolerates a missing `type` (student-if-present).
export const contactAvatar = (contact) => {
  const c = contact || {};
  const type = c.type || (c.student ? 'student' : 'guest');
  const person = type === 'teacher' ? c.teacher : c.student;
  return (person && person.avatar) || DEFAULT_AVATAR;
};

// Short relative time ("3h", "2d") for list rows / timeline stamps.
export const shortAgo = (ts) => {
  if (!ts) return '';
  const m = moment(ts);
  if (!m.isValid()) return '';
  return m.fromNow(true);
};

// Absolute stamp for message bubbles.
export const stamp = (ts) => {
  if (!ts) return '';
  const m = moment(ts);
  return m.isValid() ? m.format('D MMM, hh:mm A') : '';
};

// UI operator label ↔ backend token. The compiler accepts both space- and
// underscore-separated tokens, but we send the canonical underscore form.
export const OPERATOR_TOKEN = {
  equals: 'equals',
  'not equals': 'not_equals',
  set: 'set',
  'not set': 'not_set',
  'greater than': 'greater_than',
  'less than': 'less_than',
  between: 'between',
  contains: 'contains',
  'not contains': 'not_contains'
};

// Humanise a timeline event into the grey one-liner the thread renders.
export const humanizeEvent = (event, agentsByUuid = {}) => {
  let { payload } = event;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      payload = {};
    }
  }
  payload = payload || {};
  const actor = event.actor_cms_uuid && agentsByUuid[event.actor_cms_uuid];
  const who = actor ? actor.label : 'System';
  switch (event.type) {
    case 'status_changed':
      return `${who} changed status to ${payload.to || ''}`;
    case 'priority_changed':
      return `${who} set priority to ${payload.to || 'none'}`;
    case 'assigned_agent': {
      const to = payload.to && agentsByUuid[payload.to];
      return to ? `${who} assigned to ${to.label}` : `${who} unassigned the conversation`;
    }
    case 'assigned_team':
      return payload.to ? `${who} assigned to a team` : `${who} removed the team`;
    case 'tag_added':
      return `${who} added tag ${payload.tag || ''}`;
    case 'tag_removed':
      return `${who} removed tag ${payload.tag || ''}`;
    case 'snoozed':
      return `${who} snoozed the conversation`;
    case 'reopened':
      return `${who} reopened the conversation`;
    case 'conversation_created':
      return 'Conversation created';
    case 'auto_ack_sent':
      return 'Auto-acknowledgement sent to the customer';
    // Outbound delivery lifecycle + engagement (Resend webhooks). These are
    // System events with no actor — render a plain human label so the timeline
    // never shows the raw `System · email_opened` type string.
    case 'email_opened':
      return 'Customer opened the email';
    case 'email_clicked':
      return 'Customer clicked a link in the email';
    case 'delivery_bounced':
      return 'Email bounced';
    case 'delivery_complained':
      return 'Customer marked the email as spam';
    case 'delivery_failed':
      return 'Email delivery failed';
    case 'delivery_delayed':
      return 'Email delivery delayed';
    case 'email_suppressed':
      return 'Email suppressed (recipient on suppression list)';
    default:
      return `${who} · ${event.type}`;
  }
};

// Build the explicit mentions array (uuid + name) the notes API expects, by
// matching bootstrap agent display names present as "@Name" in the note body.
export const extractMentions = (body = '', agents = []) => {
  const out = [];
  agents.forEach((a) => {
    if (a.label && body.includes(`@${a.label}`)) {
      out.push({ cms_user_uuid: a.key, name: a.label });
    }
  });
  return out;
};

// POST /v2/support/attachments (multipart 'file'). onProgress(0..100).
// People search backed by chat_server's mod_user_external `/search` endpoint.
// `role` is 'student' | 'teacher'. Returns [{ uuid, name, email, avatar }].
export const searchUsers = (filter, role) => {
  const url = get_api_url('v1.0/users/search', false, false, true);
  return axios({
    method: 'post',
    url,
    headers: authHeaders(),
    data: { filter, role }
  }).then((res) => {
    const users = (res.data && res.data.users) || [];
    return users.map((u) => ({
      uuid: u.uuid,
      name: [u.fname, u.lname].filter(Boolean).join(' ').trim(),
      email: u.email || '',
      avatar: u.avatar || ''
    }));
  });
};

// ------------------------------------------------------------------ teams admin
// The Support Settings modal (super-user only) manages support teams and their
// members directly against the goserver /v2/support/teams endpoints. These are
// self-contained request/response calls that don't fit the list/thread saga
// model, so they live here alongside the other direct axios helpers
// (searchUsers / uploadAttachment). Every mutation rejects with the axios error
// so callers can surface the server's `reason` (e.g. duplicate team name).

// GET /v2/support/teams → [{ uuid, name, members: [{ cms_user_uuid, name }] }].
export const fetchTeams = () => {
  const url = get_api_url('/v2/support/teams', false, true);
  return axios({ method: 'get', url, headers: authHeaders() }).then(
    (res) => (res.data && res.data.data && res.data.data.teams) || []
  );
};

// GET /v2/support/agents → [{ uuid, name, email, department_uuid, department_name }].
export const fetchEligibleAgents = () => {
  const url = get_api_url('/v2/support/agents', false, true);
  return axios({ method: 'get', url, headers: authHeaders() }).then(
    (res) => (res.data && res.data.data && res.data.data.agents) || []
  );
};

// POST /v2/support/teams { name } → { uuid, name }. SUPER_USER only.
export const createTeam = (name) => {
  const url = get_api_url('/v2/support/teams', false, true);
  return axios({ method: 'post', url, headers: authHeaders(), data: { name } }).then(
    (res) => (res.data && res.data.data) || {}
  );
};

// DELETE /v2/support/teams/:uuid — cascades members + un-teams its conversations.
export const deleteTeam = (uuid) => {
  const url = get_api_url(`/v2/support/teams/${uuid}`, false, true);
  return axios({ method: 'delete', url, headers: authHeaders() });
};

// POST /v2/support/teams/:uuid/members { cms_user_uuid } — idempotent add.
export const addTeamMember = (teamUuidVal, cmsUserUuid) => {
  const url = get_api_url(`/v2/support/teams/${teamUuidVal}/members`, false, true);
  return axios({
    method: 'post',
    url,
    headers: authHeaders(),
    data: { cms_user_uuid: cmsUserUuid }
  });
};

// DELETE /v2/support/teams/:uuid/members/:cms_user_uuid.
export const removeTeamMember = (teamUuidVal, cmsUserUuid) => {
  const url = get_api_url(`/v2/support/teams/${teamUuidVal}/members/${cmsUserUuid}`, false, true);
  return axios({ method: 'delete', url, headers: authHeaders() });
};

// --------------------------------------------------------------- routing rules
// Routing rules run top-to-bottom on new inbound emails (first match wins) and
// apply team/assignee/priority/tag actions. Same super-user-only, direct-axios
// shape as the teams admin above; every mutation rejects with the axios error so
// callers can surface the server's `reason`.

// GET /v2/support/routing_rules → [{ id, match_*, action_*, is_active, sort_order }],
// ordered by sort_order.
export const fetchRoutingRules = () => {
  const url = get_api_url('/v2/support/routing_rules', false, true);
  return axios({ method: 'get', url, headers: authHeaders() }).then(
    (res) => (res.data && res.data.data && res.data.data.rules) || []
  );
};

// POST /v2/support/routing_rules { match_*, action_*, is_active } → created rule.
// At least one action required; empty strings treated as null. SUPER_USER only.
export const createRoutingRule = (payload) => {
  const url = get_api_url('/v2/support/routing_rules', false, true);
  return axios({ method: 'post', url, headers: authHeaders(), data: payload }).then(
    (res) => (res.data && res.data.data) || {}
  );
};

// PATCH /v2/support/routing_rules/:id { ...fields } — partial update; an explicit
// empty string clears a field.
export const updateRoutingRule = (id, payload) => {
  const url = get_api_url(`/v2/support/routing_rules/${id}`, false, true);
  return axios({ method: 'patch', url, headers: authHeaders(), data: payload }).then(
    (res) => (res.data && res.data.data) || {}
  );
};

// DELETE /v2/support/routing_rules/:id.
export const deleteRoutingRule = (id) => {
  const url = get_api_url(`/v2/support/routing_rules/${id}`, false, true);
  return axios({ method: 'delete', url, headers: authHeaders() });
};

// PUT /v2/support/routing_rules/reorder { ids:[...] } — full ordered id list.
export const reorderRoutingRules = (ids) => {
  const url = get_api_url('/v2/support/routing_rules/reorder', false, true);
  return axios({ method: 'put', url, headers: authHeaders(), data: { ids } });
};

// ------------------------------------------------------------ agent signature
//
// Both endpoints act on the CALLING agent's own signature — the backend resolves
// the identity from the JWT, so there is no agent id in either call. Available to
// every support agent (unlike teams/routing, which are super-user only).

// GET /v2/support/signature → { body_html, body_text }. An agent who has never
// saved one gets empty strings and a 200, not an error.
export const fetchMySignature = () => {
  const url = get_api_url('/v2/support/signature', false, true);
  return axios({ method: 'get', url, headers: authHeaders() }).then(
    (res) => (res.data && res.data.data) || {}
  );
};

// PUT /v2/support/signature { body_html } → the STORED { body_html, body_text }.
// Only the HTML is sent: the server sanitizes it and derives the plain-text
// projection itself (it is the same signature that gets appended to the
// text/plain part of every outbound email), and echoes back what it kept — so the
// caller should render the response rather than its own submitted value.
export const saveMySignature = (bodyHtml) => {
  const url = get_api_url('/v2/support/signature', false, true);
  return axios({ method: 'put', url, headers: authHeaders(), data: { body_html: bodyHtml } }).then(
    (res) => (res.data && res.data.data) || {}
  );
};

// Pull the human-readable failure reason out of an axios error (house envelope
// {"status":"failure","reason":...}), falling back to a caller-supplied default.
export const apiErrorReason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  return (data && (data.reason || data.message)) || fallback;
};

export const uploadAttachment = (file, onProgress) => {
  const url = get_api_url('/v2/support/attachments', false, true);
  const form = new FormData();
  form.append('file', file);
  return axios({
    method: 'post',
    url,
    headers: { ...authHeaders(), 'content-type': 'multipart/form-data' },
    data: form,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    }
  }).then((res) => res.data.data);
};

// GET /v2/support/conversations/:uuid/transcript?format= → triggers a download.
export const downloadTranscript = (uuid, ticketNumber, format = 'txt') => {
  const url = get_api_url(
    `/v2/support/conversations/${uuid}/transcript?format=${format}`,
    false,
    true
  );
  return axios({
    method: 'get',
    url,
    headers: authHeaders(),
    responseType: 'blob'
  }).then((res) => {
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `transcript-${ticketNumber || uuid}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  });
};
